/** The browser consumes the actual chained Hono route contract. Worker imports
 * are type-only: database/auth/provider implementations never enter the bundle. */
import { hc, type InferRequestType } from "hono/client";
import type { AppRpc } from "@worker/app";
import type { ValidationIssue } from "@shared/validation/issues";

export const createRpcClient = (fetcher: typeof fetch = (...args) => fetch(...args)) =>
  hc<AppRpc>("/", { fetch: fetcher, init: { credentials: "include" }, headers: { accept: "application/json" } });
export const rpc = createRpcClient();
export type JsonBody<Endpoint> = InferRequestType<Endpoint> extends { json: infer Input } ? Input : never;
export type QueryParams<Endpoint> = InferRequestType<Endpoint> extends { query?: infer Input } ? NonNullable<Input> : never;

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly issues?: ValidationIssue[],
    public readonly checksComplete?: boolean,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

const record = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;
const strings = (value: unknown): value is string[] => Array.isArray(value) && value.every((item) => typeof item === "string");
function isIssue(value: unknown): value is ValidationIssue {
  return record(value) && typeof value.code === "string" && ["missing", "warning", "error"].includes(String(value.kind))
    && typeof value.message === "string" && strings(value.fields) && strings(value.blocks)
    && record(value.entity) && typeof value.entity.type === "string";
}

type JsonResponse<T> = Pick<Response, "headers" | "status" | "ok"> & { json(): Promise<T> };
/** Preserve Hono's inferred response, while handling transport failures once. */
export function readJson<T>(response: JsonResponse<T> | Promise<JsonResponse<T>>): Promise<T>;
export async function readJson(response: JsonResponse<unknown> | Promise<JsonResponse<unknown>>): Promise<unknown> {
  const res = await response;
  const isJson = (res.headers.get("content-type") ?? "").includes("application/json");
  if (!res.ok) {
    const detail: unknown = isJson ? await res.json().catch(() => null) : null;
    const issues = record(detail) && Array.isArray(detail.issues) ? detail.issues.filter(isIssue) : undefined;
    const error = record(detail) && typeof detail.error === "string" ? detail.error : `Request failed (${res.status})`;
    const message = error === "validation" && issues?.length ? `${issues[0].fields.join(", ") || "input"}: ${issues[0].message}` : error;
    throw new ApiError(res.status, message, issues, record(detail) && typeof detail.checksComplete === "boolean" ? detail.checksComplete : undefined);
  }
  if (res.status === 204) return undefined;
  // A 2xx SPA fallback is not a successful API response.
  if (!isJson) throw new ApiError(res.status, "Request returned non-JSON");
  return res.json();
}

/** Better Auth owns a wildcard handler rather than the app's Hono schema.
 * Keep its two browser operations separate; business routes must use rpc. */
export function apiPost<T = unknown>(path: `/api/auth/${string}`, body?: unknown): Promise<T>;
export function apiPost(path: `/api/auth/${string}`, body: unknown = {}): Promise<unknown> {
  return readJson(fetch(path, {
    method: "POST", credentials: "include",
    headers: { accept: "application/json", "content-type": "application/json" },
    body: JSON.stringify(body),
  }));
}

export const errorMessage = (e: unknown) => (e instanceof Error ? e.message : "Something went wrong");
